import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AlertModal } from "@/components/modal/alert-modal";
import { Eye, MoreHorizontal, Trash2 } from "lucide-react";
import { showToast } from "@/components/ui/show-toast";
import { useDeleteOpportunityApplication } from "@/services/opportunities/queries";
import type { OpportunityApplication } from "@/services/types";

interface CellActionProps {
  data: OpportunityApplication;
  opportunityId?: string;
}

export const CellAction: React.FC<CellActionProps> = ({
  data,
  opportunityId,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();

  const { mutate: deleteApplication, isPending: isDeleting } =
    useDeleteOpportunityApplication(
      opportunityId || "",
      () => {
        showToast(
          "success",
          `Application from "${data.contactPersonName}" deleted successfully`
        );
        setShowDeleteModal(false);
      },
      (error: any) => {
        showToast(
          "error",
          error?.response?.data?.message ||
            "Failed to delete application. Please try again."
        );
        console.error("Delete application error:", error);
      }
    );

  const handleDelete = () => {
    if (opportunityId) {
      deleteApplication(data.id);
    }
  };

  return (
    <>
      <div onClick={(e) => e.stopPropagation()}>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => {
                if (!opportunityId) return;
                navigate(
                  `/dashboard/opportunities/${opportunityId}/applications/${data.id}`
                );
              }}
            >
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setShowDeleteModal(true)}
              className="text-red-600"
              disabled={isDeleting || !opportunityId}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Application
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AlertModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        loading={isDeleting}
        title="Delete Application"
        description={`Are you sure you want to delete the application from "${data.contactPersonName}" (${data.contactPersonEmail})? This action cannot be undone.`}
      />
    </>
  );
};
